import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { apiFetch } from '../lib/api.js';

export default function Today() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['today'],
    queryFn: () => apiFetch('/api/console/today'),
    refetchInterval: 15_000,
  });

  return (
    <div className="px-4 pt-6 pb-4">
      <header className="mb-4">
        <h1 className="d-xl text-2xl">
          Today
        </h1>
        <p className="text-sm text-[var(--ink-soft)]">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </header>

      {isLoading && <p className="text-[var(--ink-soft)]">Loading…</p>}
      {error && <p className="text-[var(--bad)]">{error.message}</p>}

      {data && (
        <div className="space-y-6">
          <Section title="Pending approvals" count={data.approvals?.length || 0}>
            {(data.approvals || []).length === 0
              ? <Empty text="Nothing waiting on you." />
              : data.approvals.map((a) => <ApprovalRow key={a.id} appointment={a} />)
            }
          </Section>

          <Section title="Today's calls" count={data.todays_calls?.length || 0}>
            {(data.todays_calls || []).length === 0
              ? <Empty text="No calls yet today. We'll text you the moment one comes in." />
              : data.todays_calls.map((c) => <CallRow key={c.id} call={c} />)
            }
          </Section>

          <Section title="Today's appointments" count={data.todays_appointments?.length || 0}>
            {(data.todays_appointments || []).length === 0
              ? <Empty text="No appointments on the books for today." />
              : data.todays_appointments.map((a) => <AppointmentRow key={a.id} appointment={a} />)
            }
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <section>
      <h2 className="mlabel mb-2 flex items-center gap-2">
        {title}
        {count > 0 && (
          <span className="stamp flat" style={{ color: 'var(--acc-ink)' }}>
            {count}
          </span>
        )}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Empty({ text }) {
  return (
    <div className="border border-dashed border-[var(--line-strong)] p-4 text-sm text-[var(--ink-soft)]">
      {text}
    </div>
  );
}

function ApprovalRow({ appointment }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, action }) =>
      apiFetch(`/api/console/appointments/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['today'] }),
  });

  const when = appointment.proposed_start_at
    ? new Date(appointment.proposed_start_at).toLocaleString([], {
        weekday: 'short', hour: 'numeric', minute: '2-digit',
      })
    : appointment.window_label;

  const urgencyBadge = appointment.urgency === 'EMERGENCY' ? 'EMERGENCY'
    : appointment.urgency === 'URGENT' ? 'URGENT' : null;

  return (
    <div className="panel p-3 space-y-2">
      <i className="brk brk-tl" /><i className="brk brk-br" />
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold">{appointment.customer_name || 'Unknown caller'}</p>
          <p className="mono text-xs text-[var(--ink-soft)]">{when}</p>
        </div>
        {urgencyBadge && (
          <span className={`stamp ${urgencyBadge === 'EMERGENCY' ? '' : 'flat'}`}
            style={{ color: urgencyBadge === 'EMERGENCY' ? 'var(--bad)' : 'var(--acc-ink)' }}>
            {urgencyBadge}
          </span>
        )}
      </div>

      {appointment.issue_summary && (
        <p className="text-sm text-[var(--ink)]">{appointment.issue_summary}</p>
      )}
      {appointment.address_line && (
        <p className="mono text-xs text-[var(--ink-soft)]">{appointment.address_line}</p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => mutation.mutate({ id: appointment.id, action: 'approve' })}
          disabled={mutation.isPending}
          className="btnx primary flex-1"
          style={{ height: '40px', fontSize: '11px' }}
        >
          <Check size={16} /> Approve
        </button>
        <button
          onClick={() => mutation.mutate({ id: appointment.id, action: 'decline' })}
          disabled={mutation.isPending}
          className="btnx flex-1"
          style={{ height: '40px', fontSize: '11px' }}
        >
          <X size={16} /> Decline
        </button>
      </div>

      {mutation.error && (
        <p className="text-xs text-[var(--bad)]">{mutation.error.message}</p>
      )}
    </div>
  );
}

function CallRow({ call }) {
  const urgencyColor = call.urgency === 'EMERGENCY' ? 'var(--bad)'
    : call.urgency === 'URGENT' ? 'var(--acc)'
    : 'var(--line)';
  return (
    <div className="lrow" style={{ borderLeft: `4px solid ${urgencyColor}` }}>
      <div>
        <p className="font-semibold">{call.customer_name || call.caller_phone || 'Unknown'}</p>
        <p className="text-xs text-[var(--ink-soft)]">{call.summary_text || '—'}</p>
      </div>
    </div>
  );
}

function AppointmentRow({ appointment }) {
  const when = appointment.confirmed_start_at || appointment.proposed_start_at;
  const time = when ? new Date(when).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';
  return (
    <div className="lrow">
      <div className="mono text-sm" style={{ color: 'var(--acc-ink)' }}>{time}</div>
      <div className="flex-1">
        <p className="font-semibold">{appointment.customer_name || 'Customer'}</p>
        <p className="text-xs text-[var(--ink-soft)]">{appointment.address_line}</p>
      </div>
    </div>
  );
}
